import React, { useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import { User, Lock, LogIn, ShieldAlert } from "lucide-react";

const AdminLogin = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login({ username, password, type: "ADMIN" });
      console.log("Logged in user:", user);
      toast.success("Admin login successful");
      navigate("/admin-dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid login credentials");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (name) =>
    `flex items-center border-2 rounded-xl px-3 transition-all duration-200 ${
      focusedField === name
        ? "border-red-700 bg-red-50/30 shadow-sm"
        : "border-gray-200 bg-white hover:border-gray-300"
    }`;

  const iconClass = (name) =>
    `mr-2 transition-colors duration-200 ${
      focusedField === name ? "text-red-700" : "text-gray-400"
    }`;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12 relative overflow-hidden">

      {/* Background decoration — darker tone for admin */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-red-100 rounded-full opacity-30 animate-pulse" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-gray-200 rounded-full opacity-40 animate-pulse" style={{ animationDelay: "1s" }} />
      </div>

      <div
        className="relative bg-white shadow-xl rounded-2xl w-full max-w-md p-8"
        style={{ animation: "slideUp 0.5s ease both" }}
      >
        {/* Header */}
        <div className="text-center mb-8" style={{ animation: "slideUp 0.5s ease 0.05s both", opacity: 0 }}>
          <div
            className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
            style={{ animation: "bounceIn 0.6s ease both" }}
          >
            <ShieldAlert size={30} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
          <p className="text-sm text-gray-500 mt-1">Restricted access — authorised personnel only</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Username */}
          <div style={{ animation: "slideUp 0.5s ease 0.12s both", opacity: 0 }}>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Username</label>
            <div className={inputClass("username")}>
              <span className={iconClass("username")}><User size={18} /></span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onFocus={() => setFocusedField("username")}
                onBlur={() => setFocusedField(null)}
                className="w-full py-2.5 outline-none bg-transparent text-sm"
                placeholder="Enter your username"
                required
              />
            </div>
          </div>

          {/* Password */}
          <div style={{ animation: "slideUp 0.5s ease 0.2s both", opacity: 0 }}>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Password</label>
            <div className={inputClass("password")}>
              <span className={iconClass("password")}><Lock size={18} /></span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
                className="w-full py-2.5 outline-none bg-transparent text-sm"
                placeholder="Enter your password"
                required
              />
            </div>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-gray-800 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
            style={{ animation: "slideUp 0.5s ease 0.28s both", opacity: 0 }}
          >
            {loading ? (
              <>
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Signing in...
              </>
            ) : (
              <>
                <LogIn size={18} />
                Login
              </>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 border-t border-gray-200" />
          <span className="text-xs text-gray-400">SECURE ACCESS</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        {/* Help */}
        <div
          className="text-center text-sm"
          style={{ animation: "slideUp 0.5s ease 0.36s both", opacity: 0 }}
        >
          <Link to="/support" className="text-gray-500 hover:text-gray-800 transition-colors duration-150">
            Need help? Contact Support
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounceIn {
          0%   { opacity: 0; transform: scale(0.5); }
          70%  { transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;